import { Suspense, useMemo, type ComponentType } from 'react';
import { AppContextProvider, AppSkeleton, type AppContextValue } from '@bliks-io/app-sdk/react';
import type { InstalledAppEntry } from './app-registry';
import { ErrorBoundary } from '@/components/error-boundary';
import { useAuthStore } from '@/stores/auth-store';
import { useProfile } from '@/api/queries/useProfile';
import { env } from '@/config/env';

interface AppContextBridgeProps {
  app: InstalledAppEntry;
  AppComponent: ComponentType;
}

export function AppContextBridge({ app, AppComponent }: AppContextBridgeProps) {
  // Tenant identity comes from the session, never from GET /api/v2/organization:
  // that read is org-manager/system-admin only, and a member holding an app seat
  // would be stuck on the skeleton forever waiting for a 403.
  const { token, tenant } = useAuthStore();
  const { data: user } = useProfile();

  const contextValue = useMemo<AppContextValue | null>(() => {
    if (!user || !tenant || !token) return null;
    return {
      user: {
        id: user._id,
        name: `${user.firstName} ${user.lastName}`,
        firstName: user.firstName,
        email: user.email,
        roles: user.userTypes ?? []
      },
      tenant: {
        id: tenant._id,
        slug: tenant.slug,
        name: tenant.name
      },
      app: {
        id: app.appId,
        config: {}
      },
      apiBaseUrl: env.BACKEND_ENDPOINT,
      accessToken: token
    };
  }, [user, tenant, app.appId, token]);

  if (!contextValue) return <AppSkeleton />;

  return (
    <AppContextProvider value={contextValue}>
      <ErrorBoundary key={app.appId}>
        <Suspense fallback={<AppSkeleton />}>
          <AppComponent />
        </Suspense>
      </ErrorBoundary>
    </AppContextProvider>
  );
}
